import type { ILojaConfig, IMelhorEnvioConfig } from "interfaces/loja";
import type { IMelhorEnvioServico } from "interfaces/loja/freteMelhorEnvio";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import { siteConfig } from "types/siteConfig";

/** Apenas dígitos (CEP, CPF, CNPJ, telefone). */
export function onlyDigits(s: string | null | undefined): string {
  return String(s ?? "").replace(/\D/g, "");
}

/**
 * CEP para o carrinho ME: só dígitos, até 8 posições, completa zeros à esquerda
 * quando houver exatamente 7 dígitos (ex.: 5291212 → 05291212).
 */
export function normalizeCepMelhorEnvioDigits(
  cepRaw: string | null | undefined
): { ok: true; cep: string } | { ok: false; message: string } {
  let d = onlyDigits(cepRaw);
  if (d.length === 7) {
    d = d.padStart(8, "0");
  }
  if (d.length > 8) {
    d = d.slice(0, 8);
  }
  if (d.length !== 8) {
    return {
      ok: false,
      message:
        "CEP do endereço precisa ter 8 dígitos. Confira no cadastro (formato pode usar hífen; o sistema remove a máscara).",
    };
  }
  return { ok: true, cep: d };
}

/**
 * Escolhe o id numérico do serviço ME a partir da lista da API e do rótulo do
 * pedido / CSV da config.
 */
export function resolveMelhorEnvioServiceId(
  servicos: IMelhorEnvioServico[],
  servicoFreteLabel: string | null | undefined,
  configServicosCsv: string | undefined
): number | null {
  const allowedIds = (configServicosCsv ?? "")
    .split(",")
    .map((x) => parseInt(String(x).trim(), 10))
    .filter((n) => !Number.isNaN(n) && n > 0);

  const pool =
    allowedIds.length > 0
      ? servicos.filter((s) => allowedIds.includes(s.id))
      : servicos;

  if (!pool.length) return null;

  const hint = (servicoFreteLabel ?? "").toLowerCase().trim();

  if (hint) {
    const byName = pool.find(
      (s) =>
        s.name.toLowerCase().includes(hint) ||
        hint.includes(s.name.toLowerCase().slice(0, 12))
    );

    if (byName) return byName.id;
  }
  return pool[0]?.id ?? null;
}

export type BuildMelhorEnvioCartInput = {
  pedido: IPedidoDetalhe;
  meConfig: IMelhorEnvioConfig;
  loja: ILojaConfig | null;
  serviceId: number;
  documentoDestinatario?: string;
  telefoneDestinatario?: string;
  emailDestinatario?: string;
};

export function buildMelhorEnvioCartPayload(
  input: BuildMelhorEnvioCartInput
): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
  const { pedido, meConfig, loja, serviceId } = input;
  const end = pedido.enderecoEntrega;
  if (!end) {
    return { ok: false, error: "Pedido sem endereço de entrega." };
  }

  const ori = normalizeCepMelhorEnvioDigits(meConfig.cepOrigem || loja?.cep);
  if (ori.ok === false) {
    return {
      ok: false,
      error:
        "CEP de origem inválido. Configure o CEP de origem no Melhor Envio ou o CEP da empresa (Configurações).",
    };
  }
  const cepOrigem = ori.cep;

  const dst = normalizeCepMelhorEnvioDigits(end.cep);
  if (dst.ok === false) {
    return { ok: false, error: dst.message };
  }
  const cepDest = dst.cep;

  const itens = pedido.itens ?? [];
  if (!itens.length) {
    return {
      ok: false,
      error: "Pedido sem itens para declarar no carrinho ME.",
    };
  }

  const products = itens.map((it) => ({
    name: String(it.produtoNome || "Item").slice(0, 500),
    quantity: String(it.quantidade ?? 1),
    unitary_value: String(Math.max(0.01, Number(it.precoUnitario)).toFixed(2)),
  }));

  const insurance_value =
    Math.round(
      itens.reduce(
        (acc, it) => acc + Number(it.precoUnitario) * (it.quantidade ?? 1),
        0
      ) * 100
    ) / 100;

  const remetenteNome =
    (loja?.razaoSocial || loja?.nomeFantasia || loja?.nomeLoja || "").trim() ||
    siteConfig.name;
  const cnpjLoja = onlyDigits(loja?.cnpj ?? "");

  const docManual = onlyDigits(input.documentoDestinatario ?? "");
  const docCadastro = onlyDigits(input.pedido.compradorCpf ?? "");
  const docDest =
    docManual.length >= 11
      ? docManual
      : docCadastro.length >= 11
        ? docCadastro
        : "";

  if (docDest.length < 11) {
    return {
      ok: false,
      error:
        "CPF ou CNPJ do destinatário é obrigatório para o Melhor Envio. Preencha o campo Documento do destinatário na expedição ou cadastre o CPF do comprador na conta.",
    };
  }

  const destNome =
    (end.nome ?? pedido.compradorNome ?? "").trim() || "Destinatário";

  const to: Record<string, unknown> = {
    name: destNome,
    phone: onlyDigits(input.telefoneDestinatario) || "11999999999",
    email:
      (input.emailDestinatario ?? "").trim() ||
      (meConfig.email || "").trim() ||
      "destinatario@example.com",
    address: (end.logradouro ?? "").trim() || "—",
    complement: (end.complemento ?? "").trim(),
    number: (end.numero ?? "").trim() || "S/N",
    district: (end.bairro ?? "").trim() || "—",
    city: (end.cidade ?? "").trim() || "—",
    postal_code: cepDest,
    country_id: (end.pais ?? "BR").trim().slice(0, 2).toUpperCase() || "BR",
    state_abbr: (end.uf ?? "").trim().toUpperCase().slice(0, 2) || "SP",
  };

  /**
   * Doc ME marca `document` e `company_document` como obrigatórios no schema;
   * omitir uma delas pode gerar erro genérico (ex.: cep inválido). Envia string vazia na não usada.
   */
  if (docDest.length >= 14) {
    to.company_document = docDest;
    to.document = "";
  } else {
    to.document = docDest;
    to.company_document = "";
    to.state_register = "ISENTO";
  }

  const phoneRemetente = onlyDigits(loja?.phoneContato ?? "");
  if (phoneRemetente.length < 10 || phoneRemetente.length > 13) {
    return {
      ok: false,
      error:
        "Telefone de contato da empresa é obrigatório para o Melhor Envio (10 a 13 dígitos). Preencha em Configurações da loja → Contato.",
    };
  }

  const emailRemetente =
    (loja?.emailContato ?? "").trim() ||
    (meConfig.email || "").trim() ||
    "contato@example.com";

  const from: Record<string, unknown> = {
    name: remetenteNome,
    phone: phoneRemetente,
    email: emailRemetente,
    document: "",
    company_document: cnpjLoja || undefined,
    state_register: (loja?.inscricaoEstadual ?? "").trim() || "ISENTO",
    address: (loja?.logradouro ?? "").trim() || "—",
    complement: (loja?.complemento ?? "").trim(),
    number: (loja?.numero ?? "").trim() || "S/N",
    district: (loja?.bairro ?? "").trim() || "—",
    city: (loja?.cidade ?? "").trim() || "—",
    postal_code: cepOrigem,
    state_abbr: (loja?.uf ?? "").trim().toUpperCase().slice(0, 2) || "SP",
    country_id: (loja?.pais ?? "BR").trim().slice(0, 2).toUpperCase() || "BR",
  };

  /** `pedidoId`: cubagem/peso calculados no backend a partir dos produtos do pedido. */
  const body: Record<string, unknown> = {
    pedidoId: pedido.id,
    service: serviceId,
    from,
    to,
    products,
    options: {
      insurance_value: insurance_value || 0.01,
      receipt: false,
      own_hand: false,
      reverse: false,
      non_commercial: true,
      platform: siteConfig.name,
      tags: [
        {
          tag: String(pedido.numero ?? pedido.id).slice(0, 120),
          Url: siteConfig.url,
        },
      ],
    },
  };

  return { ok: true, body };
}

/** Extrai o UUID do envio retornado pelo POST /cart do ME (formatos comuns de resposta). */
export function extractMelhorEnvioCartOrderId(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === "string" && data.length >= 32) return data.trim();
  if (typeof data !== "object") return null;
  const o = data as Record<string, unknown>;
  const tryId = (v: unknown) =>
    typeof v === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)
      ? v
      : null;
  const top = tryId(o.id);
  if (top) return top;
  if (Array.isArray(data) && data[0] && typeof data[0] === "object") {
    const id = tryId((data[0] as Record<string, unknown>).id);
    if (id) return id;
  }
  if (o.data && typeof o.data === "object") {
    const id = tryId((o.data as Record<string, unknown>).id);
    if (id) return id;
  }
  return null;
}

/** Extrai URL de impressão do POST `/etiquetas/imprimir` (proxy ME). */
export function extractMelhorEnvioPrintUrl(data: unknown): string | null {
  if (data == null || typeof data !== "object") return null;
  const tryUrl = (v: unknown) => {
    if (typeof v !== "string") return null;
    const t = v.trim();
    return /^https?:\/\//i.test(t) ? t : null;
  };
  const o = data as Record<string, unknown>;
  const top = tryUrl(o.url);
  if (top) return top;
  if (o.data && typeof o.data === "object") {
    const inner = tryUrl((o.data as Record<string, unknown>).url);
    if (inner) return inner;
  }
  const link = tryUrl(o.link);
  if (link) return link;
  if (o.print && typeof o.print === "object") {
    const p = tryUrl((o.print as Record<string, unknown>).url);
    if (p) return p;
  }
  return null;
}
